import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { UploadedFilesList } from '#pplx/frontend/apps/ask/ask-input/ask-input-attachments/components/UploadedFilesList.js';
import { AskInputLeftAttribution } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/components/AskInputLeftAttributions.js';
import { AskInputRightAttribution } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/components/AskInputRightAttributions.js';
import { useComputerModelChip } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useComputerModelChip.js';
import { AskInputBase } from '#pplx/frontend/apps/ask/ask-input/ask-input-base/components/AskInputBase.js';
import { useAskInputBoundary } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/components/AskInputBoundaryShared.js';
import { useAskInputAttachmentState } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputAttachmentState.js';
import { useAskInputCore } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputCore.js';
import { useAskInputFileAndPasteHandling, useAskInputFileUploadCompletion, } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputFileAndPasteHandling.js';
import { useAskInputPlaceholder } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputPlaceholder.js';
import { useComputerCommandMenuIntegration } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useComputerCommandMenuIntegration.js';
import { useComputerCommandMenuKeyDownHandlers } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useComputerCommandMenuKeyDownHandlers.js';
import { useMentionTypeahead } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useMentionTypeahead.js';
import { insertMentionNode, removeMentionNode, } from '#pplx/frontend/apps/ask/ask-input/ask-input-mentions/insertMentionNode.js';
import { AnimatedGradientBorderWrapper } from '#pplx/frontend/apps/ask/ask-input/ask-input-misc/components/AnimatedGradientBorderWrapper.js';
import { DictationBorderOverlay } from '#pplx/frontend/apps/ask/ask-input/ask-input-misc/components/DictationBorderOverlay.js';
import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { AskInputRoot } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputRoot.js';
import { QUERY_SUBMIT_DEBOUNCE_MS, normalizeAskInputSearchModeControls, } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { useComputerEffortState } from '#pplx/frontend/apps/ask/ask-input/effort/useComputerEffortState.js';
import { useThreadSubmitDisabled } from '#pplx/frontend/apps/ask/ask-input/thread-submission/useThreadSubmitDisabled.js';
import { useAskComposerTranscriptionImplementation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/askComposerTranscriptionImplementationShared.js';
import { useAskInputTranscription } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useAskInputTranscription.js';
import { useCometState, useEntropyBrowser, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { overCharacterLimitMessage } from '#pplx/frontend/apps/ask/composer-ui/inputLimitMessages.js';
import { InputOverLimitWarning } from '#pplx/frontend/apps/ask/composer-ui/InputOverLimitWarning.js';
import { replaceSubmitTabMentions } from '#pplx/frontend/apps/ask/composer-ui/normalizeSubmitMentions.js';
import { PastedTextFileNotes } from '#pplx/frontend/apps/ask/composer-ui/PastedTextFileNotes.js';
import { resolveSubmitUpload, resolveUploadAwareSubmitDisabled, useSubmitAfterUploads, useRegisterSubmit, } from '#pplx/frontend/apps/ask/composer-ui/useSubmitAfterUploads.js';
import { resolveAskInputAttachmentUploadContext, useAttachmentUploadPolicy, } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useAttachmentUploadPolicy.js';
import { useDefaultUploadGate } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useDefaultUploadGate.js';
import { useFileAttachments } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useFileAttachments.js';
import { useGetImageDownloadUrl } from '#pplx/frontend/apps/ask/hooks/file-search-hooks/useGetImageDownloadUrl.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useSidecarPreQuery } from '#pplx/frontend/apps/ask/hooks/sidecar-pre-query-hooks/useSidecarPreQuery.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { useComposerQuote } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useComposerQuote.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useResults } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useAskComposerAppearance } from '#pplx/frontend/apps/ask/providers/useAskComposerAppearance.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { useAskInputAutosuggestActions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputAutosuggestActions.js';
import { useAskInputSubmitWithMentions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSubmitWithMentions.js';
import { useAutosuggest } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAutosuggest.js';
import { useSidecarFocusInput } from '#pplx/frontend/comet/client/hooks/useSidecarFocusInput.js';
import { FileDropScopeContext } from '#pplx/frontend/common/fileDropTargets.js';
import { isImage } from '#pplx/frontend/common/files/images.js';
import { useControllableState } from '#pplx/frontend/common/hooks/useControllableState.js';
import { ConditionalWrapper } from '#pplx/frontend/components/base/ConditionalWrapper.js';
import { DropZone } from '#pplx/frontend/components/form/DropZone.js';
import { COMMIT_COMPUTER_COMMAND_QUERY_COMMAND, INSERT_COMPUTER_COMMAND_QUERY_COMMAND, } from '#pplx/frontend/components/form/lexical/plugins/ComputerCommandQueryPlugin.js';
import { MAX_CHARACTERS_ALLOWED_TO_SUBMIT } from '#pplx/frontend/components/utils/consts.js';
import { applyDictationTranscript } from '#pplx/frontend/composer-sdk-plugins/dictation/applyDictationTranscript.js';
import { decorativeGlowStyles, framedComposerGeometry, isFramedAppearance, } from '#pplx/frontend/composer-sdk/styles.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const PremiumSourceLimitWarning = Dynamic(async () => {
    const { PremiumSourceLimitWarning } = await import('#pplx/frontend/apps/ask/ask-input/ask-input-core/components/PremiumSourceLimitWarning.js');
    return { default: PremiumSourceLimitWarning };
});
const FollowUps = Dynamic(async () => {
    const { FollowupAttachments } = await import('#pplx/frontend/apps/ask/shared-ui-components/FollowupAttachments.js');
    return { default: FollowupAttachments };
});
const messages = defineMessages({
    listeningPlaceholder: {
        defaultMessage: 'Listening…',
        description: 'Placeholder text shown in the ask input when voice transcription is active',
        id: '2a4589ffa8',
    },
});
const HIDDEN_COMPUTER_MODEL_CHIP_PROPS = { showComputerModelChip: false };
function getDecorativeDropdownGlowClassNames(decorativeGlow, dropdownPlacement, shouldShowDropdown, isDashedStroke) {
    if (decorativeGlow === undefined)
        return undefined;
    if (!shouldShowDropdown)
        return undefined;
    // Glow is suppressed under the dashed stroke unless actively lit.
    if (isDashedStroke && !decorativeGlow)
        return undefined;
    return [
        decorativeGlow ? decorativeGlowStyles.dropdown : decorativeGlowStyles.idleDropdown,
        dropdownPlacement === 'bottom' ? decorativeGlowStyles.clipTop : decorativeGlowStyles.clipBottom,
    ];
}
function getDropdownClassName({ dropdownClassName, decorativeDropdownGlowClassNames, isDashedStroke, decorativeGlow, }) {
    // The dashed border itself is owned by CommandPanel via `isDashedStroke`.
    // When the glow is not actively lit, the dashed surface also flattens the
    // border color and drops the panel shadow.
    return classNames(dropdownClassName, decorativeDropdownGlowClassNames, {
        '!border-base before:!border-base !shadow-none': isDashedStroke && !decorativeGlow,
    });
}
function getWrapperClassName({ wrapperClassName, showSuggestDropdown, isIncognito, isFramed, decorativeGlow, attachedDropdownPlacement, quote, }) {
    return classNames(wrapperClassName, {
        'transition-none': showSuggestDropdown,
        'border-dashed !border-base': isIncognito && !isFramed && !decorativeGlow,
        'border-dashed': isIncognito && !isFramed && decorativeGlow,
        '!border-b-transparent rounded-b-none': attachedDropdownPlacement === 'bottom',
        '!border-t-transparent rounded-t-none': attachedDropdownPlacement === 'top',
        // Framed composers keep their frame radius: the input radius plus the frame inset.
        'rounded-b-2xl': attachedDropdownPlacement === 'top' && !isFramed,
        'bg-base': quote,
    });
}
const AskInputInner = ({ value, json, querySource, placeholder: placeholderOverride, placeholderAnimation, placeholderClassName, disableSubmission = false, disabledSubmitTooltip, disableInput = false, requireAttachment = false, initialLayout = 'expanded', minRows = 1, maxRows, isFollowUp = false, showModelSelector = true, modelSelectorOverrides, showComputerModelChip = true, searchModeControls: controlledSearchModeControls, onSearchModeSelect, onSearchModeSelected, onSearchModeCleared, showFileUpload = false, attachmentMenuPolicy, prepareFiles, attachmentExtension, showLeftAttribution = true, disableActionButtons = false, showIncognitoHint, hasShadow = false, appearance: appearanceProp, mask, framedChinVisible, decorativeGlow, autofocus = true, className, headerComponent, topHeaderComponent, nonFileAttachments, attachmentChips, registerSubmit, submissionRevision, bottomComponent, remoteBoxExecutionTarget, showStopButton = false, inFlightEntryUUID, forceHideSuggestions = false, disablePremiumSourceLimitWarning = false, onChange, onFocus, onBlur, onClick, onSubmit, createSubmitAttemptUUID, onStopButtonClick = noop, autosuggestionsEnabled = true, onUpdateAutosuggestions: controlledOnUpdateAutosuggestions, renderQueryContent, renderSuggestionItem, dropdownPlacement = 'bottom', suggestionAttachment = 'input', dropdownClassName, mentionTypeaheadOptions, mentionIconRenderer, isCometHome = false, syncUncontrolledOnce = false, onTriggerTypeahead: onTriggerTypeaheadProp, onMentionSearchQueryChange: onMentionSearchQueryChangeProp, isMissionControl, ref, onRemoveFile, onFileAttachComplete, focusOnFileAttachComplete = true, renderAttachments = true, renderComputerModeToggle, showVoice = true, showSources = true, showTabs = true, showWorkspaceFolders, workspaceDirectories: initialWorkspaceDirectories, leadingRightAttribution, additionalRightAttribution, showSubmit, onDisabledSubmit, wrapperClassName, onVoiceClick, dropBehavior = 'all-element-only', searchModeOverride, searchModel, onSearchModelSelect, onSearchModelDeselect, searchModelMenuLocalMode, searchModelTriggerAccessory, skipSearchModeGate = false, computerModelChipModelOverride, sources: controlledSources, onSourcesChange: controlledOnSourcesChange, showSkills = false, collectionUuid, computerCommandMenuSearchQuery, sideChatAvailable, onSideChatCommandSelect, onComputerCommandMenuOpen, computerCommandMenuOpen, onComputerCommandMenuOpenChange, onKeyDown: onKeyDownProp, }) => {
    const scope = useContext(FileDropScopeContext);
    const isSubmittingRef = useRef(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [uploadSelectionVersion, setUploadSelectionVersion] = useState(0);
    const [isInputActive, setIsInputActive] = useState(autofocus);
    const [isAskInputFocused, setIsAskInputFocused] = useState(false);
    const { suggestions, blankStateSuggestions, showSuggestDropdown, browserAgentAllowOnceFromToggle, forceEnableBrowserAgent, workspaceDirectories, } = useAskInputState();
    const { setSuggestions } = useAskInputActions();
    const { isMobileStyle, isMobileUserAgent } = useIsMobile();
    const { inputRef } = useAskInputState();
    const skillMentionEditorRef = useRef(null);
    const askInputEditorFocusRef = useRef(null);
    const askInputLayoutContainerRef = useRef(null);
    const [uploadsReady, setUploadsReady] = useState(true);
    const uploadedFilesListRef = useRef(null);
    const { quote, followUps } = usePerplexityQueryState();
    const { inFlight, lastResult, resultsLength } = useResults();
    const { configuredModel, sources } = useThreadConfig();
    const threadSearchMode = useSelectedSearchMode();
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    const { reason, resolvedSearchMode: selectedSearchMode, uploadSearchMode, } = useMemo(() => resolveAskInputAttachmentUploadContext({
        querySource,
        searchModeSource: searchModel === undefined
            ? { kind: 'thread-search-mode', threadSearchMode }
            : { kind: 'search-model', getSearchModeForSearchModel, searchModel },
        uploadSearchModeOverride: searchModeOverride,
    }), [getSearchModeForSearchModel, querySource, searchModeOverride, searchModel, threadSearchMode]);
    const defaultAppearance = useAskComposerAppearance(selectedSearchMode);
    const appearance = appearanceProp ?? defaultAppearance;
    const isFramed = isFramedAppearance(appearance);
    const frameAppearance = isFramed ? appearance : undefined;
    const isComputerModeFollowUp = isFollowUp && querySource !== 'fork';
    const { computerEffortPreferences, computerEffortEnabled, handleComputerEffortPreferencesChange, isComputerModeHydrating, computerHydrationCause, modeConfig, usesComputerEffortUi, } = useComputerEffortState({
        isFollowUp: isComputerModeFollowUp,
        isLocalModelSelected: searchModelMenuLocalMode?.selected === true,
        reason,
        searchModel: searchModel ?? computerModelChipModelOverride ?? configuredModel,
        selectedSearchMode,
    });
    const effectiveShowModelSelector = showModelSelector && (!isComputerModeHydrating || !isComputerModeFollowUp);
    const hydrationSubmitTooltip = useThreadSubmitDisabled({
        hydrationCause: computerHydrationCause,
        searchMode: selectedSearchMode,
    });
    const effectiveSources = controlledSources ?? sources;
    const computerModelChipProps = useComputerModelChip({
        configuredModel: computerModelChipModelOverride ?? configuredModel,
        isFollowUp: isComputerModeFollowUp,
    });
    const { specialCapabilities } = useUserCapabilities();
    const attachmentUploadPolicy = useAttachmentUploadPolicy({
        reason,
        searchMode: uploadSearchMode,
        menuPolicy: attachmentMenuPolicy,
    });
    const gateUploadFile = useDefaultUploadGate({
        checkUploadFile: attachmentUploadPolicy.gate.checkUploadFile,
        isFollowUp,
    });
    const entropyBrowser = useEntropyBrowser();
    const intl = useIntl();
    const { $t } = intl;
    const [effectiveComputerCommandMenuOpenProp, handleComputerCommandMenuOpenChangeRequest] = useControllableState({
        value: computerCommandMenuOpen,
        defaultValue: false,
        onChange: onComputerCommandMenuOpenChange,
    });
    const { activeTab, attachedTabs, actions: { clearAttachedTabs }, } = useCometState();
    const fileUploadRef = useRef(null);
    const fileHandlingRef = useRef(null);
    const [showTrimmedPasteNote, setShowTrimmedPasteNote] = useState(false);
    const [inputComputerCommandQuery, setInputComputerCommandQuery] = useState(undefined);
    // All mention/slash typeahead state (the async mention-menu open flag and the
    // synchronous bare-trigger flag) lives in this hook. The returned
    // `onTriggerTypeahead`/`onMentionSearchQueryChange` are the caller-provided
    // handlers wrapped to maintain that state while preserving the original veto
    // semantics.
    const { onTriggerTypeahead, onMentionSearchQueryChange, isMentionMenuOpened, onMentionOpen, onMentionClose, isBareMentionTrigger, } = useMentionTypeahead({
        onTriggerTypeahead: onTriggerTypeaheadProp,
        onMentionSearchQueryChange: onMentionSearchQueryChangeProp,
    });
    const suppressedMentionTypeaheadQueryRef = useRef(null);
    // Attachments
    const { cacheImageDownloadUrl } = useGetImageDownloadUrl({ reason });
    const { attachments, setAttachments } = useAskInputAttachmentState();
    const fileAttachments = useMemo(() => {
        const combined = [
            ...(attachments?.map((attachment) => ({
                url: attachment.url,
                filename: attachment.file?.name ?? 'file',
                metadata: attachment.metadata,
            })) ?? []),
            ...(attachmentExtension?.fileAttachments ?? []),
        ];
        return combined.length > 0 ? combined : undefined;
    }, [attachmentExtension?.fileAttachments, attachments]);
    const attachmentUrls = useMemo(() => fileAttachments?.map((a) => a.url), [fileAttachments]);
    const workspaceDirectoryPaths = useMemo(() => workspaceDirectories.map((directory) => directory.path), [workspaceDirectories]);
    const resolveSubmission = useCallback((params, { fromQueue }) => ({
        attachmentExtension,
        attachments,
        attachmentUrls,
        fileAttachments,
        mentions: replaceSubmitTabMentions({ activeTab, attachedTabs, mentions: params.mentions }),
        params: {
            ...params,
            ...(params.attempt && {
                attempt: { ...params.attempt, fromQueue },
            }),
        },
        workspaceDirectoryPaths,
    }), [
        attachmentExtension,
        attachments,
        attachmentUrls,
        activeTab,
        attachedTabs,
        fileAttachments,
        workspaceDirectoryPaths,
    ]);
    const submitEligibilityAttachments = useMemo(() => [
        ...(attachmentUrls ?? []),
        ...(attachmentExtension?.localFileGrants?.map((grant) => grant.grantId) ?? []),
    ], [attachmentExtension?.localFileGrants, attachmentUrls]);
    const errorMessage = useMemo(() => {
        if (value.length > MAX_CHARACTERS_ALLOWED_TO_SUBMIT) {
            return intl.formatMessage(overCharacterLimitMessage, {
                numCharacters: value.length - MAX_CHARACTERS_ALLOWED_TO_SUBMIT,
            });
        }
        return undefined;
    }, [value, intl]);
    // When a call site provides its own updater, disable the built-in suggest
    // websocket and let that callback drive suggestions.
    // TODO: lift suggestions state up to the call sites so they are fully
    // controlled, removing AskInput's direct setSuggestions/clear-on-submit.
    const websocketAutosuggest = useAutosuggest({ enabled: !controlledOnUpdateAutosuggestions });
    const handleUpdateAutosuggestions = controlledOnUpdateAutosuggestions ?? websocketAutosuggest.handleUpdateAutosuggestions;
    const { resetSuggestions, showBlankSuggestions } = useAskInputAutosuggestActions({
        blankStateSuggestions,
        handleUpdateAutosuggestions,
        selectedSearchMode,
        setSuggestions,
    });
    const stopTranscriptionOnSubmitRef = useRef(noop);
    const handleAcceptedSubmit = useCallback(({ attachmentExtension: latestAttachmentExtension, attachments: latestAttachments, attachmentUrls: latestAttachmentUrls, fileAttachments: latestFileAttachments, mentions: latestMentions, params, workspaceDirectoryPaths: latestWorkspaceDirectoryPaths, }) => {
        if (isSubmittingRef.current || isComputerModeHydrating)
            return;
        stopTranscriptionOnSubmitRef.current();
        // Clear suggestions when submitting
        resetSuggestions();
        isSubmittingRef.current = true;
        // if the query is empty and there are attachments, use the first filename as the query
        if (showFileUpload && !params.query) {
            const filename = latestFileAttachments?.[0]?.filename ??
                latestAttachmentExtension?.uploadedFiles[0]?.file.name;
            if (filename) {
                params.query = filename !== 'file' ? filename : 'File Attached';
            }
        }
        if (!errorMessage) {
            setTimeout(() => (isSubmittingRef.current = false), QUERY_SUBMIT_DEBOUNCE_MS);
            const accepted = onSubmit?.({
                ...(params.attempt && { attempt: params.attempt }),
                query: params.query,
                json: params.json,
                attachments: showFileUpload ? latestAttachmentUrls : undefined,
                fileAttachments: showFileUpload ? latestFileAttachments : undefined,
                promptSource: params.promptSource,
                querySource: params.querySource,
                ...(modeConfig && { modeConfig }),
                // The submit performed on clicking a suggestion may pass mentions as params
                ...(latestMentions && { mentions: latestMentions }),
                browserAgentAllowOnceFromToggle,
                forceEnableBrowserAgent: params.forceEnableBrowserAgent || forceEnableBrowserAgent,
                enableLearnAboutMe: params.enableLearnAboutMe,
                sourcesOverride: effectiveSources,
                localWorkspaceDirectories: latestWorkspaceDirectoryPaths,
                localFileGrants: latestAttachmentExtension?.localFileGrants,
            });
            if (accepted === false)
                return;
            latestAttachments?.forEach((attachment) => {
                if (attachment.file && attachment.file.size > 0 && isImage(attachment.url)) {
                    cacheImageDownloadUrl(attachment.url, attachment.resizedFile ?? attachment.file);
                }
            });
            fileUploadRef.current?.clearFiles();
            clearAttachedTabs();
            // Clear the auto-created text file warning on submission
            fileHandlingRef.current?.clearAutoCreatedTextFile();
            setShowTrimmedPasteNote(false);
        }
        else {
            isSubmittingRef.current = false;
        }
    }, [
        showFileUpload,
        browserAgentAllowOnceFromToggle,
        forceEnableBrowserAgent,
        cacheImageDownloadUrl,
        clearAttachedTabs,
        errorMessage,
        resetSuggestions,
        onSubmit,
        isComputerModeHydrating,
        effectiveSources,
        modeConfig,
    ]);
    const submitRequestRef = useRef(() => undefined);
    const handleSubmit = useCallback((params) => {
        submitRequestRef.current(params);
    }, []);
    const handleShouldSuppressTypeaheadTrigger = useCallback((trigger, query) => {
        const suppressedMentionTypeaheadQuery = suppressedMentionTypeaheadQueryRef.current;
        const normalizedQuery = query?.trimEnd().replace(/^@/, '');
        const shouldSuppress = trigger === '@' &&
            suppressedMentionTypeaheadQuery?.variant === 'space' &&
            normalizedQuery === suppressedMentionTypeaheadQuery.label;
        if (shouldSuppress) {
            suppressedMentionTypeaheadQueryRef.current = null;
        }
        return shouldSuppress;
    }, []);
    const handleBeforeSpaceMentionInsert = useCallback((space) => {
        suppressedMentionTypeaheadQueryRef.current = space;
    }, []);
    const handleInsertInputCommandQuery = useCallback(() => {
        skillMentionEditorRef.current?.dispatchCommand(INSERT_COMPUTER_COMMAND_QUERY_COMMAND, undefined);
    }, []);
    const handleCommitInputCommandQuery = useCallback(() => {
        setInputComputerCommandQuery(undefined);
        skillMentionEditorRef.current?.dispatchCommand(COMMIT_COMPUTER_COMMAND_QUERY_COMMAND, undefined);
    }, []);
    const handleBottomComponentInsertMentionNode = useCallback((props, options) => {
        const editor = skillMentionEditorRef.current;
        if (!editor) {
            return;
        }
        insertMentionNode(editor, props, options);
    }, []);
    const handleBottomComponentRemoveMentionNode = useCallback((props) => {
        const editor = skillMentionEditorRef.current;
        if (!editor) {
            return;
        }
        removeMentionNode(editor, props);
    }, []);
    const { open: effectiveComputerCommandMenuOpen, searchQuery: effectiveComputerCommandMenuSearchQuery, searchModeOverride: effectiveSearchModeOverride, canUseComputerCommandMenu, onInputChange: handleComputerCommandMenuInputChange, onOpenChange: handleComputerCommandMenuOpenChange, handleUpdateAutosuggestions: handleUpdateAutosuggestionsWithComputerCommandMenu, autosuggestionsEnabled: autosuggestionsEnabledWithComputerCommandMenu, onTypeaheadTrigger: handleTriggerTypeaheadWithComputerCommandMenu, onTypeaheadSearchQueryChange: handleMentionSearchQueryChangeWithComputerCommandMenu, } = useComputerCommandMenuIntegration({
        showSkills,
        selectedSearchMode,
        searchModeOverride,
        controlledSearchQuery: computerCommandMenuSearchQuery,
        inputCommandQuery: inputComputerCommandQuery,
        open: effectiveComputerCommandMenuOpenProp,
        onOpenChangeRequested: handleComputerCommandMenuOpenChangeRequest,
        onMenuOpen: onComputerCommandMenuOpen,
        onInsertInputCommandQuery: handleInsertInputCommandQuery,
        onCommitInputCommandQuery: handleCommitInputCommandQuery,
        onInputValueChange: onChange,
        onUpdateAutosuggestions: handleUpdateAutosuggestions,
        autosuggestionsEnabled,
        onTriggerTypeahead,
        onMentionSearchQueryChange,
        shouldSuppressTypeaheadTrigger: handleShouldSuppressTypeaheadTrigger,
    });
    const { isDisabled, isDisabledWithoutQuery, focusedIndex, setFocusedIndex, userInputQuery, handleSuggestionSubmission, handleFocus, handleBlur, handleChange, handleKeyDown: handleKeyDownBase, focusInput, hasQuery, } = useAskInputCore({
        value,
        json,
        querySource,
        disableSubmission: disableSubmission || isComputerModeHydrating,
        disableInput,
        requireAttachment,
        autofocus,
        hasPendingAttachments: isUploadingFile || !uploadsReady,
        hasAdditionalContent: nonFileAttachments?.canSubmit,
        onChange: handleComputerCommandMenuInputChange,
        onFocus,
        onBlur,
        handleSubmit,
        handleUpdateAutosuggestions: handleUpdateAutosuggestionsWithComputerCommandMenu,
        // When only a bare mention/slash trigger is typed (`@`/`/` with no query yet),
        // disable autosuggestions so the suggest dropdown defers to the mention popup
        // instead of flashing open. `isBareMentionTrigger` is set synchronously from
        // the plugin's `onTrigger`, so this gates the dropdown in the same tick the
        // trigger is typed — no async race with `isMentionMenuOpened`.
        autosuggestionsEnabled: autosuggestionsEnabledWithComputerCommandMenu && !isBareMentionTrigger,
        errorMessage,
        sources: effectiveSources,
        attachments: submitEligibilityAttachments,
        selectedSearchMode,
        setIsInputActive,
        isInputActive,
        inFlight,
        resultsLength,
        quote,
        // If mentions are open, we want to close suggestions to avoid overlapped
        // suggestions UIs and to re-enable scrolling for the suggestions dropdown
        isMentionMenuOpened,
        isCometHome,
        reason,
        placement: dropdownPlacement,
        onDisabledSubmit,
        onKeyDown: onKeyDownProp,
    });
    useSidecarPreQuery({
        setInput: handleChange,
    });
    useSidecarFocusInput({
        entropyBrowser,
        focusInput,
        isSidecar: !!import.meta.env.VITE_IS_SIDECAR_BUILD,
    });
    useEffect(() => {
        if (!autofocus)
            return;
        inputRef.current?.focus();
    }, [autofocus, inputRef]);
    //===========================================================================
    // Transcription
    //===========================================================================
    // The desktop app supplies native dictation behind its Eppo flag.
    const useTranscription = useAskComposerTranscriptionImplementation() ?? useAskInputTranscription;
    const { startTranscription, stopTranscription, error, isTranscribing, isInitializing, isSupported: isTranscriptionSupported, transcription, } = useTranscription({ onStart: focusInput, onStop: focusInput, reason });
    const appliedDictationRef = useRef('');
    // Set at edit and submit boundaries. Native dictation stops asynchronously, so
    // `isTranscribing` can stay true and a late chunk would refill the input.
    const isDictationDiscardedRef = useRef(false);
    useEffect(() => {
        if (!isTranscribing)
            isDictationDiscardedRef.current = false;
        const input = isTranscribing && !isDictationDiscardedRef.current ? inputRef.current : null;
        appliedDictationRef.current = applyDictationTranscript({
            adapter: input
                ? {
                    append: (delta, isInitial) => {
                        if (isInitial)
                            input.trim();
                        input.append(isInitial && input.value ? ` ${delta}` : delta);
                        handleChange(input.value, input.json);
                    },
                    replace: (text, applied) => {
                        const current = input.value;
                        const base = current.endsWith(applied) ? current.slice(0, -applied.length) : current;
                        handleChange(`${base}${text}`);
                    },
                }
                : null,
            appliedTranscript: appliedDictationRef.current,
            isTranscribing,
            transcription,
        });
    }, [transcription, isTranscribing, handleChange, inputRef]);
    const stopTranscriptionIfActive = useCallback(() => {
        if (isTranscribing) {
            isDictationDiscardedRef.current = true;
            stopTranscription();
        }
    }, [isTranscribing, stopTranscription]);
    stopTranscriptionOnSubmitRef.current = stopTranscriptionIfActive;
    const handleBeforeInput = useCallback(() => {
        // Native beforeinput only fires for user edits on the contenteditable, so
        // there is no inputType allowlist to keep in sync. Dictation appends use
        // programmatic Lexical updates, preserving the PR 43722 revert behavior.
        stopTranscriptionIfActive();
    }, [stopTranscriptionIfActive]);
    const handleKeyDown = useCallback((event) => {
        // Lexical's backspace/delete command handlers call preventDefault() on the
        // keydown event, which suppresses the subsequent beforeinput event, so we
        // must intercept here before Lexical dispatches its commands.
        if (event.key === 'Backspace' || event.key === 'Delete') {
            stopTranscriptionIfActive();
        }
        handleKeyDownBase(event);
    }, [stopTranscriptionIfActive, handleKeyDownBase]);
    const { registerKeyDownHandler: registerInputKeyDownHandler, handleKeyDown: handleKeyDownWithComputerCommandMenu, } = useComputerCommandMenuKeyDownHandlers({
        isOpen: effectiveComputerCommandMenuOpen,
        onKeyDown: handleKeyDown,
    });
    //===========================================================================
    // Attachments
    //===========================================================================
    const isAudioVideoFilesEnabled = true;
    const onStartFileUpload = useCallback(() => {
        setIsUploadingFile(true);
        setUploadSelectionVersion((version) => version + 1);
    }, []);
    const handleFileUploadHidden = useCallback(() => {
        setAttachments(undefined);
        fileUploadRef.current?.clearFiles();
    }, [setAttachments]);
    const handleCompleteFileUpload = useAskInputFileUploadCompletion({
        focusInput,
        focusOnFileAttachComplete,
        onFileAttachComplete,
        setAttachments,
        setIsUploadingFile,
    });
    const { handlePaste: handleAskInputPaste, handlePasteQuery, handleFileSelect, autoCreatedTextFile, dismissNotification, setAutoCreatedTextFile, } = useAskInputFileAndPasteHandling({
        fileUploadRef,
        fileHandlingRef,
        showFileUpload,
        attachments: submitEligibilityAttachments,
        onFileUploadHidden: handleFileUploadHidden,
        handleChange,
        gateUploadFile,
        maxFiles: attachmentUploadPolicy.constraints.maxFiles,
        preserveFileIdentity: prepareFiles !== undefined,
    });
    const handlePaste = useCallback((event) => {
        // Our <Input /> handles PASTE_COMMAND and calls event.preventDefault() on ClipboardEvent,
        // then applies its own update.
        // That suppresses native insertFromPaste 'beforeinput' event,
        // so paste is an explicit dictation stop signal alongside handleBeforeInput.
        stopTranscriptionIfActive();
        if (showFileUpload) {
            handleAskInputPaste(event);
        }
    }, [handleAskInputPaste, showFileUpload, stopTranscriptionIfActive]);
    const { errorMessage: fileUploadErrorMessage, warningMessage: fileUploadWarningMessage, uploadedFiles: uploadedRemoteFiles, addAttachmentUrl, handleRemoveFile: handleRemoveRemoteFile, handleFileInput, setErrorMessage: setAttachmentErrorMessage, } = useFileAttachments({
        fileUploadRef,
        isAudioVideoFilesEnabled,
        isFollowUp,
        lastResult,
        onStartFileUpload,
        onCompleteFileUpload: handleCompleteFileUpload,
        setUploadsReady,
        reason,
        specialCapabilities,
        askInputRef: ref,
        editorFocusRef: askInputEditorFocusRef,
        onRemoveFile,
        uploadPolicy: attachmentUploadPolicy,
        gateUploadFile,
        prepareFiles,
        additionalFileCount: attachmentExtension?.uploadedFiles.length,
    });
    const addQuoteAttachment = useCallback((attachment) => {
        addAttachmentUrl(attachment.url, attachment.filename, attachment.metadata);
    }, [addAttachmentUrl]);
    const { clearQuote } = useComposerQuote({
        isFollowUp,
        attachments: fileAttachments,
        addAttachment: addQuoteAttachment,
        focusInput,
    });
    const effectiveFileUploadErrorMessage = attachmentExtension?.errorMessage ?? fileUploadErrorMessage;
    const extensionFileIds = useMemo(() => new Set(attachmentExtension?.uploadedFiles.map((file) => file.id) ?? []), [attachmentExtension?.uploadedFiles]);
    const renderTrailingAccessory = attachmentExtension?.renderTrailingAccessory;
    const uploadedFiles = useMemo(() => [...uploadedRemoteFiles, ...(attachmentExtension?.uploadedFiles ?? [])], [attachmentExtension?.uploadedFiles, uploadedRemoteFiles]);
    const submitUploads = useMemo(() => uploadedFiles.map((file) => resolveSubmitUpload({
        hasStoredUrl: Boolean(file.nextURL),
        id: file.id,
        status: file.status,
    })), [uploadedFiles]);
    const submitResourceIds = useMemo(() => [
        ...attachedTabs.map((tab) => `tab:${tab.id}:${String(activeTab.tabId) === tab.id ? activeTab.url : tab.url}`),
        ...workspaceDirectoryPaths.map((path) => `workspace:${path}`),
    ], [activeTab, attachedTabs, workspaceDirectoryPaths]);
    const isSubmitBlocked = useCallback((params) => isDisabledWithoutQuery || (!hasQuery && (params.query?.trim().length ?? 0) === 0), [hasQuery, isDisabledWithoutQuery]);
    const submit = useSubmitAfterUploads({
        analyticsSurface: 'ask-input',
        hasUploadFailure: Boolean(effectiveFileUploadErrorMessage),
        isSubmissionBlocked: isSubmitBlocked,
        isUploadPending: isUploadingFile || !uploadsReady,
        onSubmit: handleAcceptedSubmit,
        resolveSubmission,
        submissionResourceIds: submitResourceIds,
        submissionRevision: submissionRevision ?? json ?? value,
        uploadSelectionVersion,
        uploads: submitUploads,
    });
    const { requestSubmit: requestSubmitAfterUploads } = submit;
    const requestSubmit = useCallback(({ attempt, ...params }) => requestSubmitAfterUploads({
        ...params,
        ...(createSubmitAttemptUUID && {
            attempt: {
                method: attempt?.method ?? 'unknown',
                attemptUUID: createSubmitAttemptUUID(),
                fromQueue: false,
            },
        }),
    }), [createSubmitAttemptUUID, requestSubmitAfterUploads]);
    submitRequestRef.current = requestSubmit;
    const submitWithMentions = useAskInputSubmitWithMentions({ json, handleSubmit: requestSubmit });
    const isSubmitDisabled = resolveUploadAwareSubmitDisabled({
        isDisabled,
        isSubmitBlocked: submit.isSubmitBlocked,
        isSubmitQueued: submit.isSubmitQueued,
    });
    useRegisterSubmit({
        register: registerSubmit,
        isDisabled: isSubmitDisabled,
        submit: () => submitWithMentions({ query: userInputQuery, json, querySource }),
    });
    useEffect(() => {
        return entropyBrowser.subscribeForceSubmitQuery((payload) => {
            requestSubmit(payload);
        });
    }, [entropyBrowser, requestSubmit]);
    const handleRemoveFile = useCallback((fileUrl, fileUUID, fileId) => {
        if (fileId && extensionFileIds.has(fileId)) {
            attachmentExtension?.onRemoveFile(fileUrl, fileUUID, fileId);
            return;
        }
        handleRemoveRemoteFile(fileUrl, fileUUID, fileId);
    }, [attachmentExtension, extensionFileIds, handleRemoveRemoteFile]);
    // Handle pasting text back and removing the auto-created file
    const pasteAsText = useCallback(() => {
        if (autoCreatedTextFile) {
            const input = inputRef.current;
            if (!input)
                return;
            // Append the file content to any existing input, preserving mentions and formatting
            const remainingCharacters = Math.max(0, MAX_CHARACTERS_ALLOWED_TO_SUBMIT - input.value.length);
            const contentToPaste = autoCreatedTextFile.content.slice(0, remainingCharacters);
            const wasTrimmed = contentToPaste.length < autoCreatedTextFile.content.length;
            if (contentToPaste.length > 0) {
                input.append(contentToPaste);
            }
            handleChange(input.value, input.json);
            setShowTrimmedPasteNote(wasTrimmed);
            const uploadedFile = uploadedFiles.find((file) => file.file.name === autoCreatedTextFile.fileName);
            if (uploadedFile?.nextURL) {
                handleRemoveFile(uploadedFile.nextURL, uploadedFile.file_uuid ?? '', uploadedFile.id);
            }
            else if (uploadedFile) {
                handleRemoveFile(undefined, '', uploadedFile.id);
            }
            else {
                const autoCreatedFileUrl = attachmentUrls?.find((url) => url.includes(autoCreatedTextFile.fileName));
                if (autoCreatedFileUrl) {
                    handleRemoveFile(autoCreatedFileUrl);
                }
            }
            setAutoCreatedTextFile(null);
        }
    }, [
        autoCreatedTextFile,
        inputRef,
        handleChange,
        attachmentUrls,
        uploadedFiles,
        handleRemoveFile,
        setAutoCreatedTextFile,
    ]);
    const dismissTrimmedPasteNote = useCallback(() => {
        setShowTrimmedPasteNote(false);
    }, []);
    //===========================================================================
    // Render
    //===========================================================================
    const valueRef = useRef(value);
    valueRef.current = value;
    const shouldShowDropdown = showSuggestDropdown && !forceHideSuggestions && !isTranscribing;
    useEffect(() => {
        // This effect updates blank state suggestions when a keyboard shortcut
        // change to/from incognito is used--no `handleChange` callback for the
        // keyboard shortcut since no text changes in the input
        if (shouldShowDropdown)
            showBlankSuggestions(valueRef.current);
    }, [showBlankSuggestions, shouldShowDropdown]);
    const handleClick = useCallback((e) => {
        setIsInputActive(true);
        showBlankSuggestions(valueRef.current);
        onClick?.(e);
    }, [setIsInputActive, showBlankSuggestions, onClick]);
    const refCb = useCallback((inputElement) => {
        inputRef.current = inputElement;
    }, [inputRef]);
    const { isIncognito } = useIsIncognito({ reason });
    const suggestDropdownProps = useMemo(() => {
        const isDashedStroke = isIncognito && shouldShowDropdown;
        const decorativeDropdownGlowClassNames = getDecorativeDropdownGlowClassNames(decorativeGlow, dropdownPlacement, shouldShowDropdown, isDashedStroke);
        const props = {
            isOpen: shouldShowDropdown,
            suggestedQueries: suggestions,
            focusedIndex: focusedIndex,
            setFocusedIndex: setFocusedIndex,
            handlePasteQuery: handlePasteQuery,
            handleSubmit: handleSuggestionSubmission,
            userInputQuery: userInputQuery,
            placement: dropdownPlacement,
            isDashedStroke,
            dropdownClassName: getDropdownClassName({
                dropdownClassName,
                decorativeDropdownGlowClassNames,
                isDashedStroke,
                decorativeGlow,
            }),
            allowNonSequentialMatch: suggestions !== blankStateSuggestions[selectedSearchMode],
            scrollableShards: uploadedFiles.length > 0 ? [uploadedFilesListRef] : [],
            searchModeOverride: selectedSearchMode,
            renderQueryContent,
            renderSuggestionItem,
        };
        return props;
    }, [
        blankStateSuggestions,
        dropdownClassName,
        dropdownPlacement,
        focusedIndex,
        handlePasteQuery,
        handleSuggestionSubmission,
        selectedSearchMode,
        setFocusedIndex,
        suggestions,
        uploadedFiles.length,
        userInputQuery,
        shouldShowDropdown,
        isIncognito,
        decorativeGlow,
        renderQueryContent,
        renderSuggestionItem,
    ]);
    const ResizeableInputWrapperProps = useMemo(() => {
        const shouldShowDropdown = showSuggestDropdown && !forceHideSuggestions && !isTranscribing;
        const attachedDropdownPlacement = shouldShowDropdown ? dropdownPlacement : undefined;
        const bottomContent = typeof bottomComponent === 'function'
            ? bottomComponent({
                onInsertMentionNode: handleBottomComponentInsertMentionNode,
                onRemoveMentionNode: handleBottomComponentRemoveMentionNode,
            })
            : bottomComponent;
        const props = {
            initialLayout,
            wrapperClass: getWrapperClassName({
                wrapperClassName,
                showSuggestDropdown,
                isIncognito,
                isFramed,
                decorativeGlow,
                attachedDropdownPlacement: isFramed && suggestionAttachment === 'frame' ? undefined : attachedDropdownPlacement,
                quote: !!quote && !isFramed,
            }),
            dashed: isIncognito && isFramed,
            hasShadow: hasShadow && !isIncognito,
            appearance,
            mask,
            framedChinVisible,
            attachedHeader: isFramed ? headerComponent : undefined,
            decorativeGlow,
            attachedDropdownPlacement,
            suggestionAttachment,
            isMobileUserAgent,
            showStopButton,
            quote: attachmentChips && followUps?.selections.some((item) => item.anchor)
                ? undefined
                : (quote ?? undefined),
            quotePreview: followUps?.selections.some((item) => item.anchor) ? (_jsx(FollowUps, { ...followUps, clear: clearQuote, className: "mx-3 flex" })) : undefined,
            inputWarnings: (errorMessage || autoCreatedTextFile || showTrimmedPasteNote) && (_jsxs(_Fragment, { children: [errorMessage && _jsx(InputOverLimitWarning, { errorMessage: errorMessage }), _jsx(PastedTextFileNotes, { convertedTextLength: autoCreatedTextFile?.content.length, showTrimmedPasteNote: showTrimmedPasteNote, onPasteAsText: pasteAsText, onDismissConversion: dismissNotification, onDismissTrimmed: dismissTrimmedPasteNote })] })),
            attachmentsList: showFileUpload &&
                (uploadedFiles.length > 0 || requireAttachment) &&
                renderAttachments && (_jsx(UploadedFilesList, { uploadedFiles: uploadedFiles, presentation: quote || nonFileAttachments ? 'compact' : 'media', renderTrailingAccessory: (uploadedFile) => extensionFileIds.has(uploadedFile.id) ? renderTrailingAccessory?.(uploadedFile) : null, onRemoveFile: handleRemoveFile, requireAttachment: requireAttachment, isSubmitting: isSubmittingRef.current, onFileSelect: (files) => fileUploadRef.current?.uploadFiles(files), onDrop: handleFileSelect, ref: uploadedFilesListRef })),
            setQuote: clearQuote,
            topContent: (_jsxs(_Fragment, { children: [topHeaderComponent, attachmentChips?.(uploadedFiles.length > 0)] })),
            // Framed: draw the dictation ring on the input box's own edge, not around the frame.
            // Mount it only while dictating so idle composers render exactly as before.
            inputBoxOverlay: isFramed && isTranscribing ? (_jsx(DictationBorderOverlay, { active: true, borderRadius: framedComposerGeometry.inputRadiusPx })) : undefined,
            bottomContent,
            inputRef,
            inputContainerRef: askInputLayoutContainerRef,
        };
        return props;
    }, [
        autoCreatedTextFile,
        dismissTrimmedPasteNote,
        dismissNotification,
        dropdownPlacement,
        errorMessage,
        forceHideSuggestions,
        handleFileSelect,
        handleBottomComponentInsertMentionNode,
        handleBottomComponentRemoveMentionNode,
        handleRemoveFile,
        hasShadow,
        headerComponent,
        appearance,
        mask,
        framedChinVisible,
        isFramed,
        isIncognito,
        decorativeGlow,
        initialLayout,
        inputRef,
        askInputLayoutContainerRef,
        isTranscribing,
        pasteAsText,
        quote,
        followUps,
        clearQuote,
        showFileUpload,
        showTrimmedPasteNote,
        showStopButton,
        showSuggestDropdown,
        suggestionAttachment,
        topHeaderComponent,
        nonFileAttachments,
        attachmentChips,
        bottomComponent,
        uploadedFiles,
        renderAttachments,
        renderTrailingAccessory,
        extensionFileIds,
        requireAttachment,
        isMobileUserAgent,
        wrapperClassName,
    ]);
    const transcribingPlaceholder = isTranscribing
        ? intl.formatMessage(messages.listeningPlaceholder)
        : undefined;
    const searchModeControls = useMemo(() => normalizeAskInputSearchModeControls(controlledSearchModeControls), [controlledSearchModeControls]);
    const placeholder = useAskInputPlaceholder({
        override: transcribingPlaceholder ?? placeholderOverride,
        showSearchModes: searchModeControls.enabled,
        showSources,
    });
    const ResizeableInputProps = useMemo(() => {
        const shouldShowComputerCommandQuery = canUseComputerCommandMenu &&
            (effectiveComputerCommandMenuOpenProp || inputComputerCommandQuery !== undefined);
        const computerCommandQueryPlaceholderText = canUseComputerCommandMenu
            ? $t({
                defaultMessage: 'Type to search',
                description: 'Inline hint shown after typing slash in the Computer command menu',
                id: '9869fd5d59',
            })
            : undefined;
        const props = {
            ref: refCb,
            id: 'ask-input',
            editorFocusRef: askInputEditorFocusRef,
            value,
            json,
            initialLayout,
            minRows: minRows,
            maxRows,
            placeholder: shouldShowComputerCommandQuery ? undefined : placeholder,
            placeholderAnimation,
            placeholderClassName,
            disableInput,
            autoFocus: autofocus,
            onClick: handleClick,
            onChange: handleChange,
            onKeyDown: handleKeyDownWithComputerCommandMenu,
            onBeforeInput: handleBeforeInput,
            onBlur: handleBlur,
            onFocus: handleFocus,
            onPaste: handlePaste,
            onMentionMenuOpen: onMentionOpen,
            onMentionMenuClose: onMentionClose,
            isMobileStyle,
            isMobileUserAgent,
            mentionTypeaheadOptions,
            mentionIconRenderer,
            spacesCopyVariant: 'project',
            onTriggerTypeahead: handleTriggerTypeaheadWithComputerCommandMenu,
            onMentionSearchQueryChange: handleMentionSearchQueryChangeWithComputerCommandMenu,
            computerCommandQueryEnabled: canUseComputerCommandMenu,
            computerCommandQueryPlaceholderText,
            onComputerCommandQueryChange: setInputComputerCommandQuery,
            lexicalEditorRef: skillMentionEditorRef,
        };
        return props;
    }, [
        autofocus,
        disableInput,
        handleBlur,
        handleChange,
        handleBeforeInput,
        handleClick,
        handleFocus,
        handleKeyDownWithComputerCommandMenu,
        handlePaste,
        initialLayout,
        isMobileStyle,
        isMobileUserAgent,
        json,
        canUseComputerCommandMenu,
        mentionTypeaheadOptions,
        mentionIconRenderer,
        maxRows,
        minRows,
        onMentionClose,
        onMentionOpen,
        handleTriggerTypeaheadWithComputerCommandMenu,
        handleMentionSearchQueryChangeWithComputerCommandMenu,
        effectiveComputerCommandMenuOpenProp,
        inputComputerCommandQuery,
        askInputEditorFocusRef,
        placeholder,
        placeholderAnimation,
        placeholderClassName,
        refCb,
        skillMentionEditorRef,
        $t,
        value,
    ]);
    const rightAttributionComponents = useMemo(() => (_jsx(AskInputRightAttribution, { value: value, json: json, isFollowUp: isFollowUp, isDisabled: isSubmitDisabled, isSubmitLoading: submit.isSubmitQueued, disabledSubmitTooltip: disabledSubmitTooltip ?? hydrationSubmitTooltip, querySource: querySource, handleSubmit: handleSubmit, showModelSelector: effectiveShowModelSelector, modelSelectorOverrides: modelSelectorOverrides, disableActionButtons: disableActionButtons, showStopButton: showStopButton, isAskInputFocused: isAskInputFocused, inFlightEntryUUID: inFlightEntryUUID, onStopButtonClick: onStopButtonClick, startTranscription: startTranscription, stopTranscription: stopTranscription, isTranscribing: isTranscribing, isTranscriptionInitializing: isInitializing, isTranscriptionAvailable: isTranscriptionSupported, transcriptionError: error, uploadedFiles: uploadedFiles, hasQuery: hasQuery, isCometHome: isCometHome, isMissionControl: isMissionControl, showVoice: showVoice, leadingRightAttribution: leadingRightAttribution, additionalRightAttribution: additionalRightAttribution, showSubmit: showSubmit, onVoiceClick: onVoiceClick, searchModel: searchModel, onSearchModelSelect: onSearchModelSelect, onSearchModelDeselect: onSearchModelDeselect, searchModelMenuLocalMode: searchModelMenuLocalMode, searchModelTriggerAccessory: searchModelTriggerAccessory, computerEffortPreferences: usesComputerEffortUi ? computerEffortPreferences : undefined, computerEffortEnabled: computerEffortEnabled, onComputerEffortPreferencesChange: usesComputerEffortUi ? handleComputerEffortPreferencesChange : undefined, skipSearchModeGate: skipSearchModeGate, computerModelChipProps: showComputerModelChip ? computerModelChipProps : HIDDEN_COMPUTER_MODEL_CHIP_PROPS })), [
        computerModelChipProps,
        computerEffortEnabled,
        disableActionButtons,
        disabledSubmitTooltip,
        hydrationSubmitTooltip,
        handleSubmit,
        hasQuery,
        inFlightEntryUUID,
        isAskInputFocused,
        isSubmitDisabled,
        isFollowUp,
        isInitializing,
        isTranscribing,
        isTranscriptionSupported,
        json,
        onStopButtonClick,
        querySource,
        effectiveShowModelSelector,
        modelSelectorOverrides,
        showStopButton,
        startTranscription,
        stopTranscription,
        uploadedFiles,
        submit.isSubmitQueued,
        value,
        isCometHome,
        isMissionControl,
        error,
        showVoice,
        leadingRightAttribution,
        additionalRightAttribution,
        showSubmit,
        onVoiceClick,
        searchModel,
        onSearchModelSelect,
        onSearchModelDeselect,
        searchModelMenuLocalMode,
        searchModelTriggerAccessory,
        computerEffortPreferences,
        handleComputerEffortPreferencesChange,
        usesComputerEffortUi,
        skipSearchModeGate,
        showComputerModelChip,
    ]);
    const leftAttributionComponents = useMemo(() => showLeftAttribution ? (_jsx(AskInputLeftAttribution, { remoteBoxExecutionTarget: remoteBoxExecutionTarget, disabled: disableInput, showIncognitoHint: showIncognitoHint, modeToggleAppearance: frameAppearance, searchModesEnabled: searchModeControls.enabled, searchModeToggleVisibility: searchModeControls.toggleVisibility, omittedSearchModes: searchModeControls.omittedModes, onSearchModeSelected: onSearchModeSelected, onSearchModeCleared: onSearchModeCleared, showSources: showSources, showTabs: showTabs, isFollowUp: isFollowUp, showFileUpload: showFileUpload, attachmentUploadPolicy: attachmentUploadPolicy, gateUploadFile: gateUploadFile, handleFileInput: handleFileInput, fileUploadErrorMessage: effectiveFileUploadErrorMessage, fileUploadWarningMessage: fileUploadWarningMessage, setAttachmentErrorMessage: setAttachmentErrorMessage, searchModeOverride: effectiveSearchModeOverride, onSearchModeSelect: onSearchModeSelect, sources: controlledSources, onSourcesChange: controlledOnSourcesChange, inputJson: json, showSkills: showSkills, collectionUuid: collectionUuid, skillMentionEditorRef: skillMentionEditorRef, computerCommandMenuSearchQuery: effectiveComputerCommandMenuSearchQuery, sideChatAvailable: sideChatAvailable, onSideChatCommandSelect: onSideChatCommandSelect, computerCommandMenuOpen: effectiveComputerCommandMenuOpen, onComputerCommandMenuOpenChange: handleComputerCommandMenuOpenChange, askInputLayoutContainerRef: askInputLayoutContainerRef, registerInputKeyDownHandler: registerInputKeyDownHandler, showWorkspaceFolders: showWorkspaceFolders, persistWorkspaceFolderSelection: initialWorkspaceDirectories === undefined, onBeforeSpaceMentionInsert: handleBeforeSpaceMentionInsert, renderComputerModeToggle: renderComputerModeToggle })) : null, [
        showLeftAttribution,
        remoteBoxExecutionTarget,
        disableInput,
        handleFileInput,
        isFollowUp,
        showFileUpload,
        attachmentUploadPolicy,
        gateUploadFile,
        showIncognitoHint,
        frameAppearance,
        searchModeControls,
        onSearchModeSelected,
        onSearchModeCleared,
        showSources,
        showTabs,
        effectiveFileUploadErrorMessage,
        fileUploadWarningMessage,
        setAttachmentErrorMessage,
        effectiveSearchModeOverride,
        onSearchModeSelect,
        controlledSources,
        controlledOnSourcesChange,
        json,
        showSkills,
        collectionUuid,
        effectiveComputerCommandMenuSearchQuery,
        sideChatAvailable,
        onSideChatCommandSelect,
        effectiveComputerCommandMenuOpen,
        handleComputerCommandMenuOpenChange,
        askInputLayoutContainerRef,
        registerInputKeyDownHandler,
        skillMentionEditorRef,
        showWorkspaceFolders,
        initialWorkspaceDirectories,
        handleBeforeSpaceMentionInsert,
        renderComputerModeToggle,
    ]);
    const inputContent = (_jsx(AskInputBase, { value: value, suggestDropdownProps: suggestDropdownProps, ResizeableInputWrapperProps: ResizeableInputWrapperProps, ResizeableInputProps: ResizeableInputProps, syncUncontrolledOnce: syncUncontrolledOnce, leftAttributionComponents: leftAttributionComponents, rightAttributionComponents: rightAttributionComponents }));
    const boxContent = (_jsxs("div", { className: isFramed ? undefined : 'bg-base rounded-2xl', "data-ask-input-container": true, onFocus: () => {
            setIsAskInputFocused(true);
        }, onBlur: (event) => {
            const nextFocusedElement = event.relatedTarget;
            setIsAskInputFocused(nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement));
        }, children: [!isFramed && headerComponent, _jsx(BoxPrimitive, { bg: isFramed ? undefined : 'background', 
                // Host shadows passed through className follow the frame's outer radius when framed.
                className: classNames(className, 'relative', isFramed ? framedComposerGeometry.frameRadiusClassName : 'rounded-2xl', { 'z-[2]': !isFramed && !!headerComponent }), children: _jsx("div", { className: isTranscribing && !isFramed ? '-m-px' : '', children: isTranscribing && !isFramed ? (_jsx(AnimatedGradientBorderWrapper, { borderRadius: 17, borderGradientStops: "transparent 135deg, var(--accent-fg-primary) 180deg, transparent 225deg", children: inputContent })) : (inputContent) }) })] }));
    return (_jsx(ConditionalWrapper, { condition: showFileUpload && !(requireAttachment && uploadedFiles.length === 0), Wrapper: DropZone, onDrop: handleFileSelect, dropBehavior: dropBehavior, scope: scope, 
        // Framed: round the drop overlay like the frame it covers.
        overlayFrameRef: isFramed ? askInputLayoutContainerRef : undefined, children: _jsxs(FileDropScopeContext.Provider, { value: undefined, children: [!disablePremiumSourceLimitWarning && _jsx(PremiumSourceLimitWarning, { isFollowUp: isFollowUp }), boxContent] }) }));
};
const AskInputWithBoundary = (props) => {
    const Boundary = useAskInputBoundary();
    const input = _jsx(AskInputInner, { ...props });
    return Boundary ? _jsx(Boundary, { children: input }) : input;
};
const AskInputMemo = React.memo(AskInputWithBoundary);
const AskInput = Object.assign(AskInputMemo, {
    Root: AskInputRoot,
});
export default AskInput;
//# sourceMappingURL=AskInput.js.map